<template>
  <div class="expenses-border">
    <Form
      :form-id="formId"
      :initial-values="initialValues"
      action="#"
      class="bold section-row section div-5"
      :class="{ dirty }"
    >
      <div
        class="dropdown-field -ml-2 triple-f"
        @click="toggleExpenseOpen"
      >
        <ChevronUp
          v-if="expenseCategoryExists"
          class="arrow"
          :class="{ 'down-arrow': !expenseCategoryOpen }"
        />
        <span
          v-else
          class="arrow"
        >&nbsp;</span>
        <VPInput
          :disabled="disabled"
          input-id="name"
          placeholder="Add expense category"
          class="w-full"
          @update:model-value="save"
        />
      </div>
      <div class="right-field double-f">
        {{ totalDisplay }}
      </div>

      <HiddenInput input-id="id" />
    </Form>
    <div v-show="expenseCategoryOpen">
      <component
        :is="isVariableExpense ? VariableExpensesRow : ExpenseRow"
        v-for="(k, i) in expenseKeys"
        :key="k"
        :form-id="k"
        :initial-values="(expenses && expenses[i]) || {}"
        :disabled="loading"
        :placeholder-addition="selectedCategoryName || ''"
        @blur="onRowBlur(k)"
        @save="onRowSave(k)"
        @clear="onRowDelete(k)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { Form, HiddenInput, useFormsStore } from 'vuniform';
import { computed, ref } from 'vue';
import { useOfferSheetStore } from '@/stores/offer-sheet';
import { useOfferSheetFormStore } from '@/stores/offer-sheet-form';
import { formatCurrency } from '@/helpers/currency';
import VPInput from '@/components/form/VPInput.vue';
import ExpenseRow from './ExpenseRow.vue';
import VariableExpensesRow from './VariableExpensesRow.vue';
import { UpsertExpenseInput } from '../../../generated/graphql';
import { ChevronUp } from 'mdue';

const formsStore = useFormsStore();
const offerSheetStore = useOfferSheetStore();
const offerSheetFormStore = useOfferSheetFormStore();

const expenseCategoryOpen = ref<boolean>(false);

const toggleExpenseOpen = () => {
  expenseCategoryOpen.value = !expenseCategoryOpen.value;
};

const expenseCategoryExists = computed(() => formsStore.fieldGetValue(props.formId, 'name'));
const dirty = computed(() => formsStore.isFormDirty(props.formId));

interface Props {
  formId: string;
  initialValues: unknown;
  disabled?: boolean;
  total: number;
  expenses: object[];
}

const selectedCategoryName = computed<string>(() => <string>formsStore.fieldGetValue(props.formId, 'name') || '');
const isVariableExpense = computed(() => selectedCategoryName.value.trim().toLowerCase() === 'variable');

const props = defineProps<Props>();

const emit = defineEmits<{
  (e: 'save'): void;
}>();

const save = () => {
  emit('save');
};

const totalDisplay = computed(() => {
  if (!formsStore.fieldGetValue(props.formId, 'id')) {
    return '';
  }

  const total = isNaN(props.total) ? 0 : props.total;
  return formatCurrency(total, { onZero: '-' });
});

const expenseKeys = computed(() => offerSheetFormStore.expenseFormsKeys[props.formId]);
const loading = computed(() => offerSheetStore.expensesLoading);

const deleteRow = (formId: string) => {
  offerSheetFormStore.deleteExpenseRow(formId);
};

const onRowBlur = (key: string) => {
  onRowSave(key);
};

const onRowDelete = async (formId: string) => {
  const id = formsStore.fieldGetValue(formId, 'id');
  if (!id) {
    return;
  }

  const catId = formsStore.fieldGetValue(props.formId, 'id');

  await offerSheetStore.deleteExpense(id, catId);

  formsStore.RESET_FIELD_VALUES(formId);

  deleteRow(formId);
};

const onRowSave = async (formId: string) => {
  if (loading.value || !formsStore.isFormDirty(formId)) {
    return;
  }

  const payload = formsStore.formGetValues<UpsertExpenseInput>(formId);
  payload.offerSheetId = offerSheetStore.offerSheet.id;
  payload.expenseCategoryId = +formsStore.fieldGetValue(props.formId, 'id');
  payload.clientMutationId = formId;

  const expense = await offerSheetStore.upsertExpense(payload);

  if (!expense) {
    return;
  }

  formsStore.SET_INITIAL_VALUES({
    values: { ...expense },
    formId,
    clearDirty: true,
  });

  formsStore.RESET_FIELD_VALUES(formId);
};
</script>
